import { create } from "zustand";

import kutil, { type PageInfo } from "@modules/kutil";

import { useAuthStore } from "./auth_store";

export type Settings = {
  [item: string]: any;
}

interface SettingsStoreState {
  settings: Settings,
  localSettings: Settings,
  init: () => Promise<boolean>;
  getSetting: (item: string) => any;
  setSetting: (item: string, value: any, delay?: number) => void;
  getGridState: (item: string) => any;
  setGridState: (item: string, value: any, delay?: number) => void;
  flush: () => Promise<void>;
  getLocalSetting: (item: LocalSettingItem, subItem: string) => any;
  setLocalSetting: (item: LocalSettingItem, subItem: string, value: any) => void;
  getDefWorkStationId: (path: string) => string;
  getWorkStationId: (path: string) => string;
  setWorkStationId: (path: string, workStationId: string) => void;
}

const LOCAL_SETTING_ITEMS = ["tabs", "workStationId"] as const;
type LocalSettingItem = typeof LOCAL_SETTING_ITEMS[number];
export type LocalSettings = Partial<Record<LocalSettingItem, any>>;

// 저장 정책:
// - 변경은 10초 debounce로 모아 저장, 같은 키는 최신값 우선
// - 저장 실패 시 재시도하지 않고 다음 저장에 병합 (savingSettings 잔존분)
// - 로그아웃 시 flush()로 최종 저장 시도. flush 진행 중 발생한 변경의 저장 여부는 보장하지 않음
// - 로그아웃 없이 이탈한 미저장분은 유실 허용

const EMPTY_SETTING = {};
const settings: Settings = {};
const localSettings: LocalSettings = {};
let changedSettings: Settings = {};
let savingSettings: Settings = {};
let isSaving = false;

async function save() {
  if (isSaving) { scheduleSave(1_000); return; }

  isSaving = true;
  savingSettings = { ...savingSettings, ...changedSettings };
  changedSettings = {};
  try {
    const infos: any[] = [];
    Object.keys(savingSettings).forEach(key => {
      infos.push({
        settingItem: key,
        settingValue: JSON.stringify(savingSettings[key])
      });
    });
    if (infos.length > 0) {
      await kutil.sendPost("/api/common/settings.proc?mode=save", infos, {});
      savingSettings = {};
    }
  } catch(e) {
    console.log(e);
  } finally {
    isSaving = false;
  }
}

let timerId: ReturnType<typeof setTimeout>;
function scheduleSave(delay: number) {
  clearTimeout(timerId);
  timerId = setTimeout(async () => {
    await save();
  }, delay);
}

export const useSettingsStore = create<SettingsStoreState>((set, get) => ({
  settings: settings,
  localSettings: localSettings,
  init: async () => {
    try {
      LOCAL_SETTING_ITEMS.forEach((item) => {
        try {
          const localData = kutil.getLocalData("kwms." + item + "." + useAuthStore.getState().userNo);
          if (localData) {
            localSettings[item] = JSON.parse(localData);
          }
        } catch(e) {
          console.log(e);
        }
      });

      const data = await kutil.sendGet("/api/common/settings.data?mode=search", {}, {skipLoading: true});
      const settings: Settings = {};
      data.settingInfos.forEach((info: any) => {
        try {
          settings[info.settingItem] = JSON.parse(info.settingValue);
        } catch(e) {
          settings[info.settingItem] = info.settingValue;
        }
      });
      set({ settings });
      return true;
    } catch(e) {
      console.log(e);
      return false;
    }
  },
  getSetting: (item: string): any => {
    return get().settings[item];
  },
  setSetting: (item: string, value: any, delay: number = 5_000) => {
    set((prev) => ({
      settings: { ...prev.settings, [item]: value },
    }));
    changedSettings[item] = value;
    scheduleSave(delay);
  },
  getGridState: (item: string): any => {
    return get().settings["grid." + item] ?? EMPTY_SETTING;
  },
  setGridState: (item: string, value: any, delay: number = 5_000) => {
    set((prev) => ({
      settings: { ...prev.settings, ["grid." + item]: value },
    }));
    changedSettings["grid." + item] = value;
    scheduleSave(delay);
  },
  flush: async () => {
    clearTimeout(timerId);
    while(isSaving) {
      await kutil.sleep(100);
    }
    clearTimeout(timerId);
    await save();
  },
  getLocalSetting: (item: LocalSettingItem, subItem: string): any => {
    const setting = get().localSettings[item] ?? {};
    return setting[subItem];
  },
  setLocalSetting: (item: LocalSettingItem, subItem: string, value: any) => {
    const setting = get().localSettings[item] ?? {};
    setting[subItem] = value;
    kutil.setLocalData("kwms." + item + "." + useAuthStore.getState().userNo, JSON.stringify(setting));
  },
  getDefWorkStationId: (path: string) => {
    if (useAuthStore.getState().defWorkStationId) {
      return useAuthStore.getState().defWorkStationId;
    } else {
      return get().getWorkStationId(path);
    }
  },
  getWorkStationId: (path: string) => {
    return get().getLocalSetting("workStationId", path) || "";
  },
  setWorkStationId: (path: string, workStationId: string) => {
    get().setLocalSetting("workStationId", path, workStationId);
  },
}));
